<template lang="pug">
section.step-subtitle
  h2 SEARCH PATIENT
section.search-patient
  .cols
    .col
      .form-group
        label(for="search-by") Search By
        select#search-by(v-model="searchBy")
          option(
            v-for="type in PATIENT_SEARCH_OPTIONS",
            :key="type.id"
            :value="type.value"
            :selected="type.value === searchBy"
          ) {{ type.name }}
    .col(v-if="searchBy==='name'")
      .form-group
        label(for="first-name") First Name
        input#first-name(type="text", v-model="firstName")
    .col(v-if="searchBy==='name'")
      .form-group
        label(for="last-name") Last Name
        input#last-name(type="text", v-model="lastName")
    .col(v-if="searchBy==='mrn'")
      .form-group(:class="{ 'form-group__invalid': v$.mrn.$errors.length }")
        label(for="mrn") MRN
        input#mrn(
          type="text", 
          v-model="mrn"
          @blur="v$.mrn.$touch()"
        )
        .form-group__errors(v-if="v$.mrn.$dirty && v$.mrn.$errors.length")
          .form-group__error(v-if="v$.mrn.required.$invalid") MRN is required!
    .col(v-if="searchBy==='dob'")
      .form-group(:class="{ 'form-group__invalid': v$.dob.$errors.length }")
        label(for="dob") Date of Birth
        input#dob(
          type="text", 
          v-model="dob"
          v-maska="'##/##/####'" 
          placeholder="MM/DD/YYYY" 
          @blur="v$.dob.$touch()"
        )
        .form-group__errors(v-if="v$.dob.$dirty && v$.dob.$errors.length")
          .form-group__error Date of Birth is required!
  .actions-below-table-container
    span
    BaseBtn.lg.green(
      @click.prevent="startSearch()",
      :disabled="checkFormValidations()"
    ) SEARCH
</template>

<script>
import { defineComponent } from "vue";
import { PATIENT_SEARCH_OPTIONS } from "@/helpers";
import useVuelidate from "@vuelidate/core";
import { required, minLength } from "@vuelidate/validators";
import { maska } from "maska";
import { getPatientNavigationReferral } from "@/api/patientNavigation";

export default defineComponent({
  directives: { maska },
  setup() {
    return { v$: useVuelidate() };
  },
  validations() {
    let args = {};
    if (this.searchBy == "dob") {
      args = {
        dob: {
          required,
          minLength: minLength(10),
          function() {
            let unixDistributed = new Date(this.dob).getTime();
            if (!unixDistributed) {
              return false;
            }
            return true;
          },
        },
      };
    } else if (this.searchBy == "mrn") {
      args = {
        mrn: { required },
      };
    }

    return args;
  },
  data() {
    return {
      mrn: null,
      dob: null,
      isSearch: false,
      searchBy: "name",
      firstName: "",
      lastName: "",
      searchParams: null,
      PATIENT_SEARCH_OPTIONS,
      originalAppointment: null,
    };
  },

  computed: {
    isRescheduleMode() {
      return this.$route.query.reschedule === "true";
    },
  },

  methods: {
    checkFormValidations() {
      if (this.searchBy === "name") {
        return this.lastName == "" && this.firstName == "";
      } else {
        return this.v$.$invalid;
      }
    },
    startSearch() {
      this.searchParams = {
        searchBy: this.searchBy,
        firstName: this.firstName,
        lastName: this.lastName,
        mrn: this.mrn,
        dob: this.dob,
      };

      // Include reschedule parameters if in reschedule mode
      if (this.isRescheduleMode) {
        this.searchParams.reschedule = "true";
        this.searchParams.originalId = this.$route.query.originalId;
        this.searchParams.rescheduleType = this.$route.query.rescheduleType;
      }

      this.isSearch = true;
      this.$router.push({
        name: "cco-patient-navigation.scheduling.select-patient",
        query: this.searchParams,
      });
    },

    async loadOriginalAppointmentForReschedule() {
      const originalId = this.$route.query.originalId;
      if (!originalId || !this.isRescheduleMode) return;

      try {
        const response = await getPatientNavigationReferral(originalId, {
          params: {
            include: ["patient", "communityCare"],
          },
        });

        this.originalAppointment = response.data.patientNavigation;

        if (this.originalAppointment && this.originalAppointment.patient) {
          // Pre-fill search with patient's name
          const patient = this.originalAppointment.patient;
          this.searchBy = "name";
          this.firstName = patient.first || "";
          this.lastName = patient.last || "";

          // Automatically start the search
          this.startSearch();
        }
      } catch (error) {
        console.error("Error loading original appointment:", error);
      }
    },
  },

  mounted() {
    if (this.isRescheduleMode) {
      this.loadOriginalAppointmentForReschedule();
    }

    // Pre-fill patient names if provided in query parameters
    if (this.$route.query.firstName || this.$route.query.lastName) {
      this.searchBy = "name";
      this.firstName = this.$route.query.firstName || "";
      this.lastName = this.$route.query.lastName || "";
    }
  },
});
</script>

<style scoped lang="scss">
@import "@/scss/components/actions-below-table-container.scss";
@import "@/scss/components/step-subtitle.scss";
.search-patient {
  padding: 50px 30px 9px;
  .cols {
    width: 100%;
    display: flex;
    justify-content: space-between;
    flex-wrap: wrap;
    .col {
      width: math.percentage(math.div(345, 1108));
      margin-right: 36px;
      flex-grow: 0;
      flex-shrink: 0;
      &:last-child {
        margin-right: auto;
      }
    }
  }
}
</style>
